import styled from "@emotion/styled";
import {
  Button,
  IconExpand,
  IconImageAlt2,
  IconVideo,
  Link,
  ScreenReaderText,
  Stack,
} from "atoms";
import { Image, Thumbnail } from "features/images";
import { submissionPermalinkUrl } from "features/links";
import type { OpenCallSubmission } from "../types";

type OpenCallSubmissionCardProps = {
  showArtist?: boolean;
  submission: OpenCallSubmission;
  children?: React.ReactNode;
  onClick?: VoidFunction;
};

function OpenCallSubmissionCard({
  showArtist,
  submission,
  children,
  onClick,
  ...props
}: OpenCallSubmissionCardProps) {
  const hasModal = typeof onClick === "function";
  const permalink = submissionPermalinkUrl(submission);
  return (
    <Card id={submission.submissionId} {...props}>
      <CardImage>
        {hasModal ? (
          <ModalButton onClick={onClick}>
            <ScreenReaderText>See more details</ScreenReaderText>
            <SubmissionCardMediaIcon submissionMedia={submission.submission} />
            <ExpandIcon />
          </ModalButton>
        ) : (
          <SubmissionCardMediaIcon submissionMedia={submission.submission} />
        )}
        <CardThumbnail
          src={submission.submission}
          sizes="300px"
          loading="lazy"
        />
      </CardImage>
      <CardBody>
        {children ?? (
          <Stack gap="xxs">
            <div>
              <AbsoluteLink to={permalink}>{submission.title}</AbsoluteLink>
            </div>
            {showArtist && <ArtistName>by {submission.name}</ArtistName>}
          </Stack>
        )}
      </CardBody>
    </Card>
  );
}
const Card = styled.li(({ theme }) => ({
  borderRadius: theme.borderRadius.sm,
  boxShadow: theme.boxShadow.light,
  overflow: "hidden",
  position: "relative",
  display: "grid",
  gridTemplateColumns: "100%",
  gridTemplateRows: "auto 1fr",
  transition: ".2s ease",
  transitionProperty: "box-shadow, transform",
  willChange: "box-shadow, transform",
}));

const CardImage = styled.div({
  aspectRatio: "1",
  padding: 10,
  position: "relative",
  zIndex: 3,
});

const CardThumbnail = styled(Thumbnail)({
  aspectRatio: "auto",
  width: "100%",
  height: "100%",
  transition: ".2s ease transform",
  willChange: "transform",
  img: {
    objectFit: "contain",
    height: "100%",
  },
  "&::before": {
    opacity: 0,
  },
});

const CardBody = styled.div<{ isRelative?: boolean }>(({ theme }) => ({
  ...theme.fonts.display,
  alignSelf: "stretch",
  borderTop: "1px solid",
  borderTopColor: theme.colors.fg10,
  color: theme.colors.fg,
  textAlign: "center",
  fontWeight: "bold",
  fontSize: 13.5,
  lineHeight: 1.2,
  margin: 0,
  padding: 10,
  width: "100%",
  transition: ".2s ease background-color",
  "&:has(input:checked)": {
    backgroundColor: theme.colors.fg05,
    boxShadow: `inset 0 -1px 0 0 ${theme.colors.fg10}, inset 1px 0px 0 0 ${theme.colors.fg10}, inset -1px 0 0 0 ${theme.colors.fg10}`,
  },
  "&:has(input:hover, input:focus-visible)": {
    backgroundColor: theme.colors.fg10,
  },
}));

const ModalButton = styled(Button)(({ theme }) => ({
  position: "absolute",
  borderRadius: 0,
  border: 0,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 4,
  width: "100%",
  outlineOffset: -5,
  "&, &:hover, &:focus-visible": {
    backgroundColor: theme.colors.transparent,
  },
  "&::before": {
    content: "''",
    display: "block",
    backgroundColor: theme.colors.fg,
    borderRadius: theme.borderRadius.round,
    opacity: 0,
    position: "absolute",
    width: "4rem",
    height: "4rem",
    zIndex: 3,
    transition: ".2s ease all",
    transform: "scale(.33)",
    transformOrigin: "center",
    pointerEvents: "none",
  },
  "&:hover::before, &:focus-visible::before": {
    opacity: 0.5,
    transform: "scale(1)",
  },
}));

const ExpandIcon = styled(IconExpand)(({ theme }) => ({
  height: "1.5rem",
  width: "1.5rem",
  display: "grid",
  placeItems: "center",
  opacity: 0,
  transformOrigin: "center",
  transform: "scale(.33)",
  transition: ".2s ease all",
  position: "relative",
  zIndex: 4,
  pointerEvents: "none",
  "&&": {
    maxHeight: "2rem",
    maxWidth: "2rem",
  },
  "button:hover &, a:hover &, button:focus-visible &, a:focus-visible &": {
    opacity: 1,
    transform: "scale(1)",
  },
  path: {
    fill: theme.colors.bg,
  },
}));
ExpandIcon.defaultProps = {
  "aria-hidden": "true",
};

function SubmissionCardMediaIcon({
  submissionMedia,
}: {
  submissionMedia: Image;
}) {
  if (submissionMedia.videoUrl) {
    return (
      <IconWrapper aria-hidden>
        <IconVideo />
      </IconWrapper>
    );
  }
  if (submissionMedia.url) {
    return (
      <IconWrapper aria-hidden>
        <IconImageAlt2 />
      </IconWrapper>
    );
  }
  return null;
}
const IconWrapper = styled.div(({ theme }) => ({
  position: "absolute",
  right: 10,
  bottom: 0,
  backgroundColor: theme.colors.bg,
  transform: "translateY(50%)",
  zIndex: 4,
  "&, svg": {
    color: theme.colors.fg30,
    display: "block",
    height: 14,
    width: 14,
  },
}));

const ArtistName = styled.span(({ theme }) => ({
  ...theme.fonts.body,
  fontSize: 12,
  fontWeight: "normal",
}));

const AbsoluteLink = styled(Link)({
  display: "-webkit-box",
  lineClamp: "2",
  overflow: "hidden",
  textOverflow: "ellipsis",
  WebkitLineClamp: "2",
  WebkitBoxOrient: "vertical",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 5,
  },
});

export { OpenCallSubmissionCard };
